import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import ConfirmModal from '../components/ConfirmModal';
import useAdminAuth from '../hooks/useAdminAuth';
import { ArrowLeft, GraduationCap, Mail, Phone, MapPin, Calendar, Trash2, Filter, Search, RefreshCw } from 'lucide-react';
import { formatDateAU } from '../utils/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'new',       label: 'New',       cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  { value: 'contacted', label: 'Contacted', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  { value: 'enrolled',  label: 'Enrolled',  cls: 'bg-green-100 text-green-800 border-green-300' },
  { value: 'declined',  label: 'Declined',  cls: 'bg-gray-200 text-gray-700 border-gray-300' }
];

const AdminPilotAccreditations = () => {
  const navigate = useNavigate();
  useAdminAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchApps = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pilot-accreditation-applications`);
      setApps(res.data || []);
    } catch (err) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchApps(); }, []);

  const updateStatus = async (appId, newStatus) => {
    try {
      await axios.put(`${API}/pilot-accreditation-applications/${appId}`, { status: newStatus });
      setApps(prev => prev.map(a => a.id === appId ? { ...a, status: newStatus } : a));
      toast.success(`Marked as ${newStatus}`);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/pilot-accreditation-applications/${confirmDelete.id}`);
      setApps(prev => prev.filter(a => a.id !== confirmDelete.id));
      toast.success('Application deleted');
    } catch (err) {
      toast.error('Failed to delete');
    } finally {
      setConfirmDelete(null);
    }
  };

  // Filter + search
  const filtered = apps.filter(a => {
    if (filter !== 'all' && a.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      const blob = `${a.name} ${a.email} ${a.phone} ${a.address} ${a.course_title}`.toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  const counts = {
    all: apps.length,
    new: apps.filter(a => a.status === 'new').length,
    contacted: apps.filter(a => a.status === 'contacted').length,
    enrolled: apps.filter(a => a.status === 'enrolled').length,
    declined: apps.filter(a => a.status === 'declined').length
  };

  const StatusBadge = ({ status }) => {
    const meta = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${meta.cls}`}>
        {meta.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="bg-[#1e3a5f] text-white py-6">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <GraduationCap className="w-7 h-7 text-red-300" />
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Pilot Accreditation Applications</h1>
              <p className="text-xs text-blue-200">Course enquiries submitted via the landing page</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/admin')}
            variant="outline"
            className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f]"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Filter + search row */}
        <Card className="p-4 mb-5">
          <div className="flex flex-wrap gap-2 items-center justify-between">
            <div className="flex flex-wrap gap-1.5">
              {[
                { v: 'all',       label: 'All' },
                { v: 'new',       label: 'New' },
                { v: 'contacted', label: 'Contacted' },
                { v: 'enrolled',  label: 'Enrolled' },
                { v: 'declined',  label: 'Declined' }
              ].map(o => (
                <Button
                  key={o.v}
                  size="sm"
                  variant={filter === o.v ? 'default' : 'outline'}
                  onClick={() => setFilter(o.v)}
                  data-testid={`filter-${o.v}`}
                  className={filter === o.v ? 'bg-[#1e3a5f]' : ''}
                >
                  <Filter className="w-3 h-3 mr-1" />
                  {o.label} ({counts[o.v] ?? 0})
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, email, course…"
                  className="pl-7 w-64"
                  data-testid="search-input"
                />
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={fetchApps}
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-3 h-3 mr-1" /> Refresh
              </Button>
            </div>
          </div>
        </Card>

        {/* Application list */}
        {loading ? (
          <Card className="p-12 text-center text-gray-500">Loading applications…</Card>
        ) : filtered.length === 0 ? (
          <Card className="p-12 text-center text-gray-500" data-testid="empty-state">
            <GraduationCap className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p>No applications match your filter.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map(app => (
              <Card key={app.id} className="p-4" data-testid={`app-card-${app.id}`}>
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-bold text-[#1e3a5f] text-lg truncate">{app.name}</h3>
                      <StatusBadge status={app.status} />
                    </div>
                    <p className="text-sm text-gray-700 font-semibold mb-2">
                      <GraduationCap className="w-4 h-4 inline mr-1 text-red-500" />
                      {app.course_title}
                    </p>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-700">
                      <div className="flex items-center gap-1">
                        <Mail className="w-3.5 h-3.5 text-gray-400" />
                        <a className="hover:text-[#1e3a5f] underline" href={`mailto:${app.email}`}>{app.email}</a>
                      </div>
                      <div className="flex items-center gap-1">
                        <Phone className="w-3.5 h-3.5 text-gray-400" />
                        <a className="hover:text-[#1e3a5f] underline" href={`tel:${app.phone}`}>{app.phone}</a>
                      </div>
                      <div className="flex items-center gap-1 sm:col-span-2">
                        <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span>{app.address}</span>
                      </div>
                      <div className="flex items-center gap-1 sm:col-span-2 text-xs text-gray-500">
                        <Calendar className="w-3 h-3" />
                        Submitted: {formatDateAU(app.created_at)}
                      </div>
                      {app.notes && (
                        <div className="sm:col-span-2 mt-1 text-xs text-gray-600 italic">
                          Notes: {app.notes}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Status actions */}
                <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-gray-100">
                  <div className="flex flex-wrap gap-1.5">
                    <Label className="text-xs text-gray-500 mr-1 self-center">Status:</Label>
                    {STATUS_OPTIONS.map(s => (
                      <Button
                        key={s.value}
                        size="sm"
                        variant={app.status === s.value ? 'default' : 'outline'}
                        onClick={() => updateStatus(app.id, s.value)}
                        disabled={app.status === s.value}
                        className={app.status === s.value ? 'bg-[#1e3a5f] hover:bg-[#1e3a5f]' : ''}
                        data-testid={`status-${s.value}-${app.id}`}
                      >
                        {s.label}
                      </Button>
                    ))}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirmDelete(app)}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    data-testid={`delete-btn-${app.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title="Delete application?"
        message={confirmDelete ? `Delete the application from ${confirmDelete.name}? This cannot be undone.` : ''}
        confirmText="Delete"
        variant="danger"
      />

      <Footer />
    </div>
  );
};

export default AdminPilotAccreditations;
